import React, { useEffect, useState } from "react";
import qs from "qs";
import moment from "moment";
import swal from "sweetalert2";
import Table from "../../common/table";
import SearchBox from "../../common/searchBox";
import CS from "../../../services/commonService";
import { useStore } from "../../../zustand/store";
import { useGetRewardLedger, useReverseRewardLedgerEntry } from "../../../queryHooks/useRewards";
import { formatPoints } from "./rewardConfig";

const TYPE_OPTIONS = [
	{ label: "Earned", value: "CREDIT", color: "success" },
	{ label: "Redeemed", value: "REDEEM", color: "warning" },
	{ label: "Reversal", value: "REVERSAL", color: "danger" },
	{ label: "Adjustment", value: "ADJUSTMENT", color: "secondary" },
];
const typeMeta = (value) => TYPE_OPTIONS.find((t) => t.value === value) || { label: value, color: "secondary" };

// Earned points, redemptions and adjustments can be reversed once; reversals themselves cannot.
const isReversible = (entry) => ["CREDIT", "REDEEM", "ADJUSTMENT"].includes(entry.type) && !entry.reversedBy;

const userLabel = (entry) => {
	const name = [entry.user?.firstName, entry.user?.lastName].filter(Boolean).join(" ");
	return name || entry.user?.email || entry.userId;
};

const Ledger = () => {
	const setLoading = useStore((state) => state.setLoading);
	const [searchTerm, setSearchTerm] = useState("");
	const [typeFilter, setTypeFilter] = useState("");
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [tableData, setTableData] = useState({ records: [], total_items: 0, page: 1, items_per_page: 10 });

	const payload = {
		page: pageNow,
		limit: itemsPerPage,
		...(searchTerm ? { search: searchTerm } : {}),
		...(typeFilter ? { type: typeFilter } : {}),
	};
	const { data, isLoading, refetch } = useGetRewardLedger(qs.stringify(payload));
	useEffect(() => {
		if (data?.status === "success") setTableData(data.data);
	}, [data]);

	const { mutateAsync: reverseEntry } = useReverseRewardLedgerEntry();
	const handleReverse = async (entry) => {
		const { value: reason } = await swal.fire({
			title: `Reverse ${formatPoints(Math.abs(entry.points))}?`,
			html: `This adds an opposite entry for <b>${userLabel(entry)}</b>. The original entry is kept for audit.${
				entry.type === "REDEEM" ? "<br/>The user's credit from this redemption will be cancelled." : ""
			}`,
			input: "textarea",
			inputPlaceholder: "Reason (required, kept in the ledger)",
			showCancelButton: true,
			confirmButtonText: "Reverse",
			inputValidator: (value) => (!value || value.trim().length < 3 ? "Please enter a reason" : undefined),
		});
		if (!reason) return;
		setLoading(true);
		try {
			const res = await reverseEntry({ id: entry.id, reason: reason.trim() });
			CS.showSuccessMessage(res.message);
			refetch();
		} catch (error) {
			CS.showErrorMessage(error?.response?.data?.message || "Failed to reverse ledger entry");
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			label: "Date & Time",
			type: "string",
			path: "createdAt",
			content: (item) => moment(item.createdAt).format("YYYY-MM-DD HH:mm"),
		},
		{
			label: "User",
			type: "string",
			path: "userId",
			content: (item) => (
				<span title={item.userId}>
					{userLabel(item)}
					{item.user?.email && <div className="text-muted small">{item.user.email}</div>}
				</span>
			),
		},
		{
			label: "Type",
			type: "string",
			path: "type",
			content: (item) => <span className={`status-badge ${typeMeta(item.type).color}`}>{typeMeta(item.type).label}</span>,
		},
		{
			label: "Description",
			type: "string",
			path: "description",
			content: (item) => (
				<span>
					{item.description || "-"}
					{item.reason && <div className="text-muted small">Reason: {item.reason}</div>}
					{item.reversedBy && <div className="text-danger small">Reversed</div>}
				</span>
			),
		},
		{
			label: "Points",
			type: "number",
			path: "points",
			content: (item) => (
				<span className={item.points > 0 ? "text-success" : "text-danger"}>
					{item.points > 0 ? "+" : ""}
					{item.points.toLocaleString()}
				</span>
			),
		},
		{
			label: "Balance After",
			type: "number",
			path: "balanceAfter",
			content: (item) => item.balanceAfter.toLocaleString(),
		},
		{
			label: "Actions",
			type: "string",
			content: (item) =>
				isReversible(item) ? (
					<button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleReverse(item)}>
						Reverse
					</button>
				) : (
					"-"
				),
		},
	];

	return (
		<div className="module-section">
			<div className="section-header">
				<h3>Points Ledger</h3>
				<div className="header-stats">
					<div className="stat-card">
						<div className="stat-label">Entries</div>
						<div className="stat-value">{(tableData.total_items || 0).toLocaleString()}</div>
					</div>
				</div>
			</div>

			<div className="section-filters">
				<SearchBox
					search={(value) => {
						setPageNow(1);
						setSearchTerm(value);
					}}
					searchValue={searchTerm}
				/>
				<select
					className="form-control"
					value={typeFilter}
					onChange={(e) => {
						setPageNow(1);
						setTypeFilter(e.target.value);
					}}
					style={{ maxWidth: "180px" }}
				>
					<option value="">All Types</option>
					{TYPE_OPTIONS.map((t) => (
						<option key={t.value} value={t.value}>
							{t.label}
						</option>
					))}
				</select>
			</div>

			<Table
				columns={columns}
				columnKey="rewards_ledger"
				tableData={tableData}
				loading={isLoading}
				setCurrentPage={(page) => setPageNow(page)}
				setCurrentLimit={(limit) => {
					setPageNow(1);
					setItemsPerPage(limit);
				}}
			/>
		</div>
	);
};

export default Ledger;
